import { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { motion, AnimatePresence } from 'motion/react';
import { Flex, IconButton, Icon, useReducedMotion, Input } from '@nula/core-ui';
import { useShallow } from 'zustand/shallow';
import { useUIStore } from '@/stores/use-ui-store';
import styles from './editor-find.module.css';

interface FoundRange {
  range: Range;
}

/**
 * Find every case-insensitive occurrence of `query` in `root`, returning a DOM
 * Range per match. Engine-neutral (TreeWalker + Range) — ported verbatim from
 * the legacy find-plugin.
 */
export function findAllRanges(root: HTMLElement, query: string): FoundRange[] {
  if (!query) return [];
  const results: FoundRange[] = [];
  const lower = query.toLowerCase();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: globalThis.Text | null;
  while ((node = walker.nextNode() as globalThis.Text | null)) {
    const text = (node.textContent ?? '').toLowerCase();
    let idx = 0;
    while ((idx = text.indexOf(lower, idx)) !== -1) {
      const range = document.createRange();
      range.setStart(node, idx);
      range.setEnd(node, idx + query.length);
      results.push({ range });
      idx += query.length;
    }
  }
  return results;
}

function renderOverlays(
  container: HTMLElement,
  overlayLayer: HTMLDivElement,
  ranges: FoundRange[],
  activeIdx: number,
) {
  overlayLayer.innerHTML = '';
  const containerRect = container.getBoundingClientRect();
  ranges.forEach(({ range }, i) => {
    const rects = range.getClientRects();
    for (let r = 0; r < rects.length; r++) {
      const rect = rects[r];
      const mark = document.createElement('div');
      mark.className = i === activeIdx ? styles.markActive : styles.mark;
      mark.style.left = `${rect.left - containerRect.left + container.scrollLeft}px`;
      mark.style.top = `${rect.top - containerRect.top + container.scrollTop}px`;
      mark.style.width = `${rect.width}px`;
      mark.style.height = `${rect.height}px`;
      overlayLayer.appendChild(mark);
    }
  });
}

export interface EditorFindProps {
  editor: Editor;
}

/**
 * Find-in-document bar (Cmd/Ctrl+F). Overlays absolutely-positioned highlight
 * marks over matches, with match count + next/prev nav. Ported from the legacy
 * find-plugin — the only engine touchpoint is the editor root element
 * (`editor.view.dom`); everything else is DOM/Range-based.
 */
export function EditorFind({ editor }: EditorFindProps) {
  const shouldReduce = useReducedMotion();
  const { isFindBarVisible, setFindBarVisible } = useUIStore(
    useShallow((s) => ({ isFindBarVisible: s.isFindBarVisible, setFindBarVisible: s.setFindBarVisible })),
  );
  const [query, setQuery] = useState('');
  const [matchCount, setMatchCount] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const rangesRef = useRef<FoundRange[]>([]);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const rootEl = () => editor.view.dom as HTMLElement;

  // Close on click outside.
  useEffect(() => {
    if (!isFindBarVisible) return;
    const handler = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setFindBarVisible(false);
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 50);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handler); };
  }, [isFindBarVisible, setFindBarVisible]);

  // Create / destroy the overlay layer.
  useEffect(() => {
    const root = rootEl();
    if (!root) return;
    const scrollParent = (root.closest('[data-scroll-container]') as HTMLElement | null) ?? root.parentElement;
    if (!scrollParent) return;
    if (getComputedStyle(scrollParent).position === 'static') scrollParent.style.position = 'relative';

    const layer = document.createElement('div');
    layer.className = styles.overlayLayer;
    scrollParent.appendChild(layer);
    overlayRef.current = layer;
    return () => {
      layer.remove();
      overlayRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // Focus input on open; clear on close.
  useEffect(() => {
    if (isFindBarVisible) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setMatchCount(0);
      setActiveIndex(0);
      rangesRef.current = [];
      if (overlayRef.current) overlayRef.current.innerHTML = '';
    }
  }, [isFindBarVisible]);

  // Cmd/Ctrl+F.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        if (isFindBarVisible) {
          inputRef.current?.focus();
          inputRef.current?.select();
        } else {
          setFindBarVisible(true);
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isFindBarVisible, setFindBarVisible]);

  // Find + highlight.
  useEffect(() => {
    if (!isFindBarVisible) return;
    const root = rootEl();
    if (!root || !overlayRef.current) return;
    const scrollParent = (root.closest('[data-scroll-container]') as HTMLElement | null) ?? root.parentElement;
    if (!scrollParent) return;

    const ranges = findAllRanges(root, query);
    rangesRef.current = ranges;
    setMatchCount(ranges.length);

    const idx = ranges.length > 0 ? Math.min(activeIndex, ranges.length - 1) : 0;
    renderOverlays(scrollParent, overlayRef.current, ranges, idx);

    if (ranges[idx]) {
      const rect = ranges[idx].range.getBoundingClientRect();
      const parentRect = scrollParent.getBoundingClientRect();
      if (rect.top < parentRect.top + 40 || rect.bottom > parentRect.bottom) {
        ranges[idx].range.startContainer.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          if (overlayRef.current && scrollParent) renderOverlays(scrollParent, overlayRef.current, ranges, idx);
        }, 300);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, query, activeIndex, isFindBarVisible]);

  const goNext = useCallback(() => {
    if (matchCount === 0) return;
    setActiveIndex((p) => (p + 1) % matchCount);
  }, [matchCount]);

  const goPrev = useCallback(() => {
    if (matchCount === 0) return;
    setActiveIndex((p) => (p - 1 + matchCount) % matchCount);
  }, [matchCount]);

  const close = useCallback(() => setFindBarVisible(false), [setFindBarVisible]);

  return (
    <AnimatePresence>
      {isFindBarVisible && (
        <motion.div
          ref={barRef}
          initial={shouldReduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
          animate={shouldReduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={shouldReduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
          transition={{ duration: 0.15 }}
        >
          <div className={styles.bar}>
            <Input
              ref={inputRef}
              size="sm"
              name="find"
              aria-label="Find in document"
              label="Find in document..."
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); if (e.shiftKey) goPrev(); else goNext(); }
                if (e.key === 'Escape') close();
              }}
              className={styles.findInput}
            />
            <div aria-live="polite" aria-atomic="true" className={styles.matchCounter}>
              {query && (matchCount > 0 ? `${activeIndex + 1} / ${matchCount}` : 'No results')}
            </div>
            <Flex gap="xs">
              <IconButton size="sm" onClick={goPrev} aria-label="Previous match (Shift+Enter)">
                <Icon name="expand_less" size={14} weight={300} />
              </IconButton>
              <IconButton size="sm" onClick={goNext} aria-label="Next match (Enter)">
                <Icon name="expand_more" size={14} weight={300} />
              </IconButton>
              <IconButton size="sm" onClick={close} aria-label="Close (Esc)">
                <Icon name="close" size={14} weight={300} />
              </IconButton>
            </Flex>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
